import React, { useState } from 'react'
import './GameLobby.css'

export type LobbyMode = 'local' | 'ai' | 'network'
type Difficulty = 'easy' | 'medium' | 'hard'

const AVATARS = [
  'adam','alex','bella','beth','carl','charlie','diana','donna',
  'eric','ethan','faith','fiona','gary','george','hannah','holly',
  'ian','ivan','jane','julia','kevin','kyle','laura','lily',
  'marcus','mike','nina','nora','oscar','owen','pam','petra',
  'quinn','rachel','ray','sam','sara','tina','tom','uma',
  'umar','vera','vince','wade','wendy','xena','yale','zara',
]

interface GameLobbyProps {
  gameTitle: string
  gameIcon: string
  gameDescription?: string
  gameBanner?: string
  supportedModes?: LobbyMode[]
  showPlayer2Name?: boolean
  initialJoinCode?: string
  networkLoading?: boolean
  networkError?: string | null
  /** Set to the session code when waiting for opponent — triggers waiting room view */
  sessionCode?: string
  onBack: () => void
  onStartLocal: (playerName: string, player2Name?: string) => void
  onStartAI: (playerName: string, difficulty: Difficulty) => void
  onCreateNetwork: (playerName: string) => void
  onJoinNetwork: (playerName: string, code: string) => void
  onCancelWaiting?: () => void
  /** Render extra setup fields inside the setup card, after the name/difficulty inputs */
  extraSetupContent?: (mode: LobbyMode) => React.ReactNode
}

const MODE_INFO: Record<LobbyMode, { icon: string; title: string; desc: string; badge: string }> = {
  local:   { icon: '🏠', title: 'Pass & Play',  desc: 'Take turns on this device',   badge: '2 players' },
  ai:      { icon: '🤖', title: 'vs Computer',  desc: 'Challenge the AI opponent',   badge: 'Solo'      },
  network: { icon: '🌐', title: 'Play Online',  desc: 'Invite a friend to join',     badge: 'Online'    },
}

function randomAvatar() {
  return AVATARS[Math.floor(Math.random() * AVATARS.length)]
}

function AvatarGrid({ selected, onSelect }: { selected: string; onSelect: (av: string) => void }) {
  return (
    <div className="gl-av-grid-wrap">
      <div className="gl-av-grid">
        {AVATARS.map(av => (
          <button
            key={av}
            type="button"
            className={`gl-av-cell${av === selected ? ' active' : ''}`}
            onClick={() => onSelect(av)}
            title={av}
          >
            <img src={`/avatars/${av}.svg`} alt={av} loading="lazy" />
          </button>
        ))}
      </div>
    </div>
  )
}

export function GameLobby({
  gameTitle,
  gameIcon,
  gameDescription,
  gameBanner = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  supportedModes = ['local', 'ai', 'network'],
  showPlayer2Name = false,
  initialJoinCode = '',
  networkLoading = false,
  networkError,
  sessionCode,
  onBack,
  onStartLocal,
  onStartAI,
  onCreateNetwork,
  onJoinNetwork,
  onCancelWaiting,
  extraSetupContent,
}: GameLobbyProps) {
  const singleMode = supportedModes.length === 1 ? supportedModes[0] : null
  const [step, setStep] = useState<'mode' | 'setup'>(
    initialJoinCode || singleMode ? 'setup' : 'mode'
  )
  const [mode, setMode] = useState<LobbyMode>(
    initialJoinCode ? 'network' : singleMode ?? 'local'
  )
  const [playerName, setPlayerName] = useState('')
  const [player2Name, setPlayer2Name] = useState('')
  const [difficulty, setDifficulty] = useState<Difficulty>('medium')
  const [joinCode, setJoinCode] = useState(initialJoinCode)
  const [avatar, setAvatar] = useState<string>(randomAvatar)
  const [p2Avatar, setP2Avatar] = useState<string>(randomAvatar)
  const [showAvatarPicker, setShowAvatarPicker] = useState(false)
  const [showP2AvatarPicker, setShowP2AvatarPicker] = useState(false)
  const [copied, setCopied] = useState(false)

  const canStart = playerName.trim().length > 0

  const handleModeSelect = (m: LobbyMode) => {
    setMode(m)
    setStep('setup')
  }

  const handleStart = () => {
    if (mode === 'local') onStartLocal(playerName.trim(), player2Name.trim() || undefined)
    else if (mode === 'ai') onStartAI(playerName.trim(), difficulty)
    else if (mode === 'network') onCreateNetwork(playerName.trim())
  }

  const handleJoin = () => {
    if (!joinCode.trim()) return
    onJoinNetwork(playerName.trim(), joinCode.trim().toUpperCase())
  }

  const handleShare = async () => {
    const url = `${window.location.origin}${window.location.pathname}?join=${sessionCode}`
    if (navigator.share) {
      try { await navigator.share({ title: `Join my ${gameTitle} game!`, url }) } catch (_) { /* dismissed */ }
    } else {
      navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) })
    }
  }

  const handleCopyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?join=${sessionCode}`
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) })
  }

  // ── Waiting Room ──────────────────────────────────────────────────────────
  if (sessionCode) {
    return (
      <div className="gl-root">
        <div className="gl-banner compact" style={{ background: gameBanner }}>
          <button className="gl-back-btn" onClick={onCancelWaiting || onBack}>←</button>
          <span className="gl-banner-icon">{gameIcon}</span>
          <h2 className="gl-banner-title">{gameTitle}</h2>
          <span className="gl-mode-chip">🌐 Online</span>
        </div>
        <div className="gl-body">

          {/* Player cards */}
          <div className="gl-waiting-players">
            <div className="gl-player-card ready">
              <div className="gl-pc-avatar">
                <img src={`/avatars/${avatar}.svg`} alt={avatar} />
              </div>
              <span className="gl-pc-name">{playerName || 'You'}</span>
              <span className="gl-pc-status gl-pc-ready">✓ Ready</span>
            </div>
            <div className="gl-players-vs">VS</div>
            <div className="gl-player-card pending">
              <div className="gl-pc-avatar gl-pc-avatar-empty">
                <span>?</span>
              </div>
              <span className="gl-pc-name gl-pc-name-dim">Opponent</span>
              <span className="gl-pc-status gl-pc-waiting">
                <span className="gl-dots-sm"><span /><span /><span /></span>
              </span>
            </div>
          </div>

          {/* Code block */}
          <div className="gl-card gl-waiting-card">
            <p className="gl-waiting-sub">Share this code to invite a friend</p>
            <div className="gl-code-block">
              <p className="gl-code-label">Session Code</p>
              <div className="gl-code-value">{sessionCode}</div>
            </div>
            <div className="gl-share-row">
              {'share' in navigator && (
                <button className="gl-btn gl-btn-primary" onClick={handleShare}>
                  📱 Share Invite
                </button>
              )}
              <button
                className={`gl-btn ${'share' in navigator ? 'gl-btn-secondary' : 'gl-btn-primary'}`}
                onClick={handleCopyLink}
              >
                {copied ? '✓ Copied!' : '📋 Copy Link'}
              </button>
            </div>
            <button className="gl-btn gl-btn-ghost" onClick={onCancelWaiting || onBack}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Mode Selection ────────────────────────────────────────────────────────
  if (step === 'mode') {
    return (
      <div className="gl-root">
        <div className="gl-banner" style={{ background: gameBanner }}>
          <button className="gl-back-btn" onClick={onBack}>←</button>
          <span className="gl-banner-icon large">{gameIcon}</span>
          <h1 className="gl-banner-title large">{gameTitle}</h1>
          {gameDescription
            ? <p className="gl-banner-sub">{gameDescription}</p>
            : <p className="gl-banner-sub">Choose how to play</p>
          }
        </div>
        <div className="gl-body">
          <div className="gl-mode-list">
            {supportedModes.map(m => (
              <button key={m} className="gl-mode-card" onClick={() => handleModeSelect(m)}>
                <span className="gl-mode-icon">{MODE_INFO[m].icon}</span>
                <div className="gl-mode-text">
                  <strong>{MODE_INFO[m].title}</strong>
                  <span>{MODE_INFO[m].desc}</span>
                </div>
                <span className="gl-mode-badge">{MODE_INFO[m].badge}</span>
                <span className="gl-mode-arrow">›</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // ── Setup ─────────────────────────────────────────────────────────────────
  return (
    <div className="gl-root">
      <div className="gl-banner compact" style={{ background: gameBanner }}>
        {singleMode
          ? <button className="gl-back-btn" onClick={onBack}>←</button>
          : <button className="gl-back-btn" onClick={() => setStep('mode')}>←</button>
        }
        <span className="gl-banner-icon">{gameIcon}</span>
        <h2 className="gl-banner-title">{gameTitle}</h2>
        <span className="gl-mode-chip">{MODE_INFO[mode].icon} {MODE_INFO[mode].title}</span>
      </div>

      <div className="gl-body">
        <div className="gl-card">

          {/* ── Player 1 ── */}
          <div className="gl-field">
            <label className="gl-label">
              {mode === 'local' ? 'Player 1' : 'Your Profile'}
            </label>
            <div className="gl-player-row">
              <button
                type="button"
                className="gl-av-thumb"
                onClick={() => { setShowAvatarPicker(o => !o); setShowP2AvatarPicker(false) }}
                title="Choose avatar"
              >
                <img src={`/avatars/${avatar}.svg`} alt={avatar} />
                <span className="gl-av-edit">✏</span>
              </button>
              <input
                className="gl-input"
                type="text"
                placeholder={mode === 'local' ? 'Player 1 name' : 'Your name'}
                value={playerName}
                onChange={e => setPlayerName(e.target.value)}
                maxLength={20}
                autoFocus
              />
            </div>
            {showAvatarPicker && (
              <AvatarGrid
                selected={avatar}
                onSelect={av => { setAvatar(av); setShowAvatarPicker(false) }}
              />
            )}
          </div>

          {/* ── Player 2 (local mode) ── */}
          {showPlayer2Name && mode === 'local' && (
            <div className="gl-field">
              <label className="gl-label">Player 2</label>
              <div className="gl-player-row">
                <button
                  type="button"
                  className="gl-av-thumb"
                  onClick={() => { setShowP2AvatarPicker(o => !o); setShowAvatarPicker(false) }}
                  title="Choose avatar"
                >
                  <img src={`/avatars/${p2Avatar}.svg`} alt={p2Avatar} />
                  <span className="gl-av-edit">✏</span>
                </button>
                <input
                  className="gl-input"
                  type="text"
                  placeholder="Player 2 name (optional)"
                  value={player2Name}
                  onChange={e => setPlayer2Name(e.target.value)}
                  maxLength={20}
                />
              </div>
              {showP2AvatarPicker && (
                <AvatarGrid
                  selected={p2Avatar}
                  onSelect={av => { setP2Avatar(av); setShowP2AvatarPicker(false) }}
                />
              )}
            </div>
          )}

          {/* ── Difficulty (AI mode) ── */}
          {mode === 'ai' && (
            <div className="gl-field">
              <label className="gl-label">Difficulty</label>
              <div className="gl-diff-row">
                {(['easy', 'medium', 'hard'] as Difficulty[]).map(d => (
                  <button
                    key={d}
                    className={`gl-diff-btn${difficulty === d ? ' active' : ''}`}
                    onClick={() => setDifficulty(d)}
                  >
                    {d === 'easy' ? '😊' : d === 'medium' ? '😐' : '😤'}
                    <span>{d.charAt(0).toUpperCase() + d.slice(1)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Game-specific extras ── */}
          {extraSetupContent?.(mode)}

          {/* ── Actions ── */}
          {mode !== 'network' && (
            <button className="gl-btn gl-btn-primary" onClick={handleStart} disabled={!canStart}>
              🎮 Start Game
            </button>
          )}

          {mode === 'network' && (
            <>
              <button
                className="gl-btn gl-btn-primary"
                onClick={handleStart}
                disabled={!canStart || networkLoading}
              >
                {networkLoading ? '⏳ Creating...' : '➕ Create Game'}
              </button>

              <div className="gl-divider"><span>or join with code</span></div>

              <div className="gl-join-row">
                <input
                  className="gl-input gl-code-input"
                  type="text"
                  placeholder="ABC123"
                  value={joinCode}
                  onChange={e => setJoinCode(e.target.value.toUpperCase())}
                  maxLength={6}
                />
                <button
                  className="gl-btn gl-btn-secondary"
                  onClick={handleJoin}
                  disabled={!canStart || !joinCode.trim() || networkLoading}
                >
                  {networkLoading ? '⏳' : 'Join →'}
                </button>
              </div>

              {networkError && <p className="gl-error">⚠️ {networkError}</p>}
            </>
          )}

          {!singleMode && (
            <button className="gl-btn gl-btn-ghost" onClick={() => setStep('mode')}>
              ← Change mode
            </button>
          )}

        </div>
      </div>
    </div>
  )
}
